import { useEffect, useRef, useState } from 'react';
import { useExamenPendiente } from '../../examenPendiente/examenPendiente';
import GustavoAura from '../../../components/GustavoAura';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Dialog, IconButton, Stack, alpha, Tooltip, useMediaQuery, Typography, Fade, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useAppSelector } from '../../../hooks/useAppStore';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useRecording }    from '../context/RecordingContext';
import { useSinReporteDeBugs } from '../hooks/useSinReporteDeBugs';
import { useUploadBugAttachmentMutation, type BugAttachment } from '../../../store/api/platformApi';
import { ChatPanel }       from './ChatPanel';
import { useHistorialGustavo } from '../hooks/useHistorialGustavo';
import { CargandoHilo }    from './CargandoHilo';
import { GustavoAssistant } from './GustavoAssistant';
import { MessageBanner } from './MessageBanner';
import { SupportPanel }    from './SupportPanel';
import { ReportBugDialog } from './ReportBugDialog';
import { LowBalanceBanner } from './LowBalanceBanner';
import { useMarkGustavoNoticesSeenMutation, useGetGustavoNoticesQuery, useGetMainConversationQuery } from '../../../store/api/gustavoApi';
import { useGetMyWhatsappStatusQuery } from '../../../store/api/usersApi';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useGetMyCreditsQuery } from '../../../store/api/creditsApi';

/** El mismo retrato que usan las burbujas del chat. El cutout de cuerpo entero que había
 *  acá se veía como una figurita diminuta al recortarlo en 38 px. */
/** Mac muestra ⌘K; el resto, Ctrl+K. */
const ES_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const ATAJO = ES_MAC ? '⌘K' : 'Ctrl+K';

const ALLOWED_ROLES =['booster_admin', 'tenant_admin', 'tenant_agent', 'office_admin', 'agent', 'notary_sale', 'notary_rent', 'read_only'];

export function GustavoWidget() {
  const golden = P.brand.golden;
  const user = useAppSelector((s) => s.auth.user);
  // ⛔ En Imperium ya no se reportan bugs (23/10/2026): ver useSinReporteDeBugs.
  const sinReporte = useSinReporteDeBugs();
  const gustavoFlagOn = useFeatureFlag('gustavo-ai');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { phase: recordingPhase } = useRecording();
  const [open,               setOpen]               = useState(false);
  const [reportOpen,         setReportOpen]         = useState(false);
  // Examen de Tutoriales del CRM pendiente: sin reportar, sin tickets ni Ayuda (ver features/examenPendiente).
  const examenPendiente = useExamenPendiente();
  const examenPendienteRef = useRef(examenPendiente);
  examenPendienteRef.current = examenPendiente;
  // Vista del panel: el reporte no abre un diálogo encima, entra deslizándose desde la
  // derecha. El chat queda montado atrás, así al volver la conversación sigue intacta.
  const [vista,              setVista]              = useState<'chat' | 'reportar'>('chat');
  const [reportKind,         setReportKind]         = useState<'bug' | 'suggestion'>('bug');
  const [pendingAttachment,  setPendingAttachment]  = useState<BugAttachment | null>(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError,    setAttachmentError]    = useState(false);
  const [uploadAttachment] = useUploadBugAttachmentMutation();
  const [autoPrompt, setAutoPrompt] = useState<string | undefined>(undefined);
  const [chatKey, setChatKey] = useState(0);
  // Gustavo tiene UN solo hilo por agente, compartido con WhatsApp: el backend dice
  // cuál es. Arrancamos con el del localStorage para pintar algo sin esperar la red,
  // pero el hilo real manda apenas responde (ver efecto más abajo). Antes esto era
  // solo localStorage y por eso el CRM abría una charla vieja, sin nada de lo que el
  // agente venía hablando desde el celular.
  const [activeConvId, setActiveConvId] = useState<string | undefined>(
    () => localStorage.getItem('gustavo:widget-conv') ?? undefined,
  );
  /**
   * QUÉ hilo hay que bajar del backend. No es lo mismo que `activeConvId`: ese cambia
   * también cuando la charla en curso estrena conversación, y ahí no hay nada que traer —
   * pedirlo remontaría el chat en la mitad de la conversación. Solo se toca al abrir el
   * widget o al saltar a otro hilo.
   */
  const [convAcargar, setConvAcargar] = useState<string | undefined>(
    () => localStorage.getItem('gustavo:widget-conv') ?? undefined,
  );
  const historial = useHistorialGustavo(convAcargar, { activo: open });
  const initialMessages = historial.mensajes;
  const loadingInitial  = historial.cargandoInicial;
  // El saldo va en el encabezado: es el dato que el agente mira antes de largarse a
  // pedirle cosas, y tenerlo escondido en otra pantalla no ayudaba.
  const { data: creditos } = useGetMyCreditsQuery(undefined, { skip: !user });

  useEffect(() => {
    // Limpiar marca legacy de "open" en sessionStorage. Si quedó pegada de
    // versiones previas, ocultaba el FAB indefinidamente al recargar.
    sessionStorage.removeItem('gustavo:widget-open');
    // Al abrir se vuelve a bajar la última página del hilo: pudo haber hablado con Gustavo
    // por WhatsApp mientras el CRM estaba cerrado. (Lo dispara `activo` en el hook.)
    // Al cerrar volvemos al chat: si no, la próxima vez que abra Gustavo se encuentra
    // con el formulario de reporte y no con la conversación.
    if (!open) setVista('chat');
  }, [open]);

  // Abrió el chat → los avisos quedan leídos y se apaga el puntito del botón. Va acá y
  // no en el click del FAB para que también valga cuando el chat se abre por otra vía.
  // El hilo único del agente (compartido CRM+WhatsApp) y los avisos sin leer.
  const { data: mainConv } = useGetMainConversationQuery(undefined, { skip: !user || !open });
  const [markNoticesSeen] = useMarkGustavoNoticesSeenMutation();
  // Comparte caché con el badge del FAB (mismo endpoint sin args), así que leer el
  // contador acá no cuesta un request extra — y evita un POST en cada apertura.
  const { data: noticesState } = useGetGustavoNoticesQuery(undefined, { skip: !user });
  // Estado de la ventana de 24 h: decide si mostrar la invitación a WhatsApp. Se pide
  // solo con el widget abierto — no hace falta saberlo hasta que lo va a ver.
  const { data: wa } = useGetMyWhatsappStatusQuery(undefined, { skip: !user || !open });

  // Qué conversación abrir. UN solo efecto decide, con prioridad explícita: si están
  // separados en dos, cada uno pisa al otro en cada render y el chat entra en loop.
  //   1. El hilo donde quedó un aviso sin leer (el punto rojo tiene que llevar al
  //      mensaje; si no, el agente abre y no encuentra nada).
  //   2. El hilo principal del agente — lo normal.
  // Los dos suelen ser el MISMO desde la unificación; la prioridad importa para los
  // avisos viejos, que quedaron en hilos de antes.
  const objetivoConvId = (noticesState?.count ? noticesState.conversationId : null) ?? mainConv?.conversationId;
  useEffect(() => {
    if (!open || !objetivoConvId || objetivoConvId === activeConvId) return;
    setActiveConvId(objetivoConvId);
    setConvAcargar(objetivoConvId);
  }, [open, objetivoConvId, activeConvId]);

  // Abrió el chat → los avisos quedan leídos.
  useEffect(() => {
    if (open && noticesState?.count) markNoticesSeen();
  }, [open, noticesState?.count, markNoticesSeen]);
  useEffect(() => {
    if (activeConvId) localStorage.setItem('gustavo:widget-conv', activeConvId);
    else              localStorage.removeItem('gustavo:widget-conv');
  }, [activeConvId]);

  // Atajo de teclado global:
  // - Cmd/Ctrl + G abre el widget desde cualquier pantalla
  // - Escape lo cierra (solo si está abierto y el foco no está en un input)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // K es el atajo que se muestra en el encabezado (el estándar de los command
      // palettes); G se mantiene por la gente que ya lo tenía en los dedos.
      if ((e.metaKey || e.ctrlKey) && ['g', 'k'].includes(e.key.toLowerCase())) {
        const el = document.activeElement as HTMLElement | null;
        const escribiendo = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
        // Escribiendo en un campo, K le pertenece a ese campo — salvo que Gustavo ya
        // esté abierto, donde el atajo sirve para cerrarlo sin sacar las manos.
        if (escribiendo && !open) return;
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      if (e.key === 'Escape' && open) {
        const active = document.activeElement as HTMLElement | null;
        const isTyping = active && (
          active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable
        );
        if (!isTyping) setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Auto-minimize while screen recording so the widget doesn't obstruct the capture
  useEffect(() => {
    if (recordingPhase === 'recording') setOpen(false);
  }, [recordingPhase]);

  // Open bug report from the recording pill — upload blob here, pass result to dialog
  useEffect(() => {
    const handler = async (e: Event) => {
      if (examenPendienteRef.current) return;
      const detail = (e as CustomEvent<{ blob?: Blob | null; kind?: 'bug' | 'suggestion' }>).detail;
      const blob = detail?.blob ?? null;
      setReportKind(detail?.kind ?? 'bug');
      setReportOpen(true);
      if (!blob || blob.size === 0) return;
      setAttachmentUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', blob, 'recording.webm');
        const att = await uploadAttachment(fd).unwrap();
        setPendingAttachment(att);
      } catch {
        setAttachmentError(true);
      } finally {
        setAttachmentUploading(false);
      }
    };
    window.addEventListener('bugreport:open', handler);
    return () => window.removeEventListener('bugreport:open', handler);
  }, [uploadAttachment]);

  useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener('gustavo:close', handler);
    return () => window.removeEventListener('gustavo:close', handler);
  }, []);

  // Escucha el evento gustavo:open despachado por NotificationBell (y otros)
  useEffect(() => {
    const handler = (e: Event) => {
      const prompt = (e as CustomEvent<{ prompt?: string }>).detail?.prompt;
      setOpen(true);
      setActiveConvId(undefined);
      setConvAcargar(undefined);
      if (prompt) {
        setAutoPrompt(prompt);
        setChatKey((k) => k + 1);
      }
    };
    window.addEventListener('gustavo:open', handler);
    return () => window.removeEventListener('gustavo:open', handler);
  }, []);

  const handleConversationChange = (id: string) => {
    // Viene del chat en curso: NO se toca `convAcargar` — lo que está en pantalla ya es esa
    // conversación, y bajarla de nuevo remontaría el chat en el medio.
    setActiveConvId(id);
  };

  if (!user) return null;
  if (!ALLOWED_ROLES.includes(user.role)) return null;
  if (!gustavoFlagOn) return null;

  // En mobile, dentro del chat el FAB de Gustavo tapaba el botón de enviar → lo ocultamos ahí.
  const hideFab = isMobile && pathname.startsWith('/chat');

  return (
    <>
      {/* Banner flotante arriba cuando llega un mensaje de Gustavo — estés donde estés. */}
      <MessageBanner />

      {/* Gustavo asistente — avatar animado + sugerencia contextual on-demand (no invasivo) */}
      {!open && !hideFab && <GustavoAssistant onOpenChat={() => setOpen(true)} />}

      <ReportBugDialog
        open={reportOpen && !examenPendiente}
        initialKind={reportKind}
        pendingAttachment={pendingAttachment}
        attachmentUploading={attachmentUploading}
        attachmentError={attachmentError}
        onClose={() => { setReportOpen(false); setPendingAttachment(null); setAttachmentError(false); }}
      />

      {/* Command center: panel centrado con el CRM difuminado detrás. Antes era un
          drawer lateral con un encabezado de tres íconos que ni siquiera decía
          "Gustavo" — no comunicaba con quién estabas hablando ni qué podía hacer. */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullScreen={isMobile}
        TransitionComponent={Fade}
        transitionDuration={180}
        sx={{ zIndex: (t) => t.zIndex.drawer + 3 }}
        BackdropProps={{
          sx: {
            // El blur es el que hace que el foco sea 100% Gustavo: el CRM sigue ahí
            // como contexto pero deja de competir por la atención.
            backdropFilter: 'blur(6px)',
            bgcolor: alpha('#000', 0.55),
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 640 },
            height: { xs: '100%', sm: 'min(78vh, 760px)' },
            m: 0,
            borderRadius: { xs: 0, sm: 3 },
            bgcolor: 'background.paper',
            border: { xs: 'none', sm: `1px solid ${alpha(golden, 0.28)}` },
            boxShadow: { xs: 'none', sm: `0 24px 80px ${alpha('#000', 0.6)}, 0 0 0 1px ${alpha(golden, 0.06)}` },
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          },
        }}
      >
        {/* Encabezado con identidad: quién es, si está disponible y cuánto le queda al
            agente para gastar. El saldo vivía en otra pantalla y era justo el dato que
            se mira antes de pedirle algo. */}
        <Box sx={{
          px: 2, py: 1.5,
          borderBottom: `1px solid ${alpha(golden, 0.15)}`,
          background: `linear-gradient(180deg, ${alpha(golden, 0.07)}, transparent)`,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              {/* La MISMA cara con aura que en la web pública. Acá era la foto con un
                  borde fino: dos representaciones distintas del mismo agente. El aura
                  toma el color de la marca activa, así que en Imperium sigue dorada. */}
              <GustavoAura size={38} ring={2} />
              <Box sx={{
                position: 'absolute', right: 1, bottom: 1, width: 10, height: 10, borderRadius: '50%',
                bgcolor: '#22c55e', border: '2px solid', borderColor: 'background.paper',
              }} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2, color: 'text.primary' }}>
                Gustavo
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                en línea
                {creditos && !creditos.unlimited && ` · ${Math.round(Math.max(0, creditos.balance)).toLocaleString('es-UY')} créditos`}
                {/* El atajo va acá y no en una píldora aparte: el encabezado ya tiene
                    avatar, estado, Reportar y tres acciones, y una insignia más lo
                    saturaba. Se aprende una vez y después no hace falta verlo. */}
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, opacity: 0.75 }}>
                  {` · ${ATAJO}`}
                </Box>
              </Typography>
            </Box>

            {/* Reportar CON la palabra, no solo el ícono: un bicho suelto no le dice a
                nadie dónde se reporta un problema, y es lo primero que preguntan. Sigue
                siendo discreto —contorno, sin relleno— para no volver a competir con la
                conversación como el bloque anterior. */}
            {examenPendiente || sinReporte ? <Box /> : (
            <Tooltip title={vista === 'chat' ? 'Reportar un problema o sugerir una mejora' : 'Volver a la conversación'}>
              <Button
                size="small"
                startIcon={vista === 'chat'
                  ? <BugReportOutlinedIcon sx={{ fontSize: 16 }} />
                  : <ArrowBackIcon sx={{ fontSize: 16 }} />}
                onClick={() => { setReportKind('bug'); setVista((v) => (v === 'chat' ? 'reportar' : 'chat')); }}
                sx={{
                  mr: 0.5, py: 0.25, px: 1, minWidth: 0,
                  fontSize: 11.5, fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
                  color: alpha('#FBBF24', 0.9),
                  border: `1px solid ${alpha('#FBBF24', 0.3)}`,
                  '&:hover': { bgcolor: alpha('#FBBF24', 0.1), borderColor: alpha('#FBBF24', 0.55) },
                  '& .MuiButton-startIcon': { mr: 0.5 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {vista === 'chat' ? 'Reportar' : 'Volver'}
                </Box>
              </Button>
            </Tooltip>
            )}

            {/* Acá había un "+" de nueva conversación. El agente tiene UN hilo (CRM +
                WhatsApp) y el efecto de arriba lo devolvía al principal al instante: el
                botón no hacía nada. Charlas aparte se abren en pantalla completa. (30/10/2026) */}
            <Stack direction="row" spacing={0.5}>
              {!examenPendiente && (
              <Tooltip title="Abrir en pantalla completa">
                <IconButton
                  size="small"
                  onClick={() => { setOpen(false); navigate('/help?tab=gustavo'); }}
                  sx={{ color: 'text.secondary', '&:hover': { color: '#fff', bgcolor: alpha(golden, 0.08) } }}
                >
                  <OpenInFullIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              )}
              <Tooltip title="Cerrar">
                <IconButton
                  size="small"
                  onClick={() => setOpen(false)}
                  sx={{ color: 'text.secondary', '&:hover': { color: '#F87171', bgcolor: alpha('#F87171', 0.08) } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* Ventana de WhatsApp cerrada → invitación a reabrirla.
            Meta solo deja escribir libremente dentro de las 24 h del último mensaje
            DEL AGENTE. Pasadas, los avisos de Gustavo le quedan solo acá y hay que
            entrar al CRM para verlos — hoy le pasa a 130 de 142 agentes. Un mensaje
            suyo, del contenido que sea, reabre la ventana por 24 h.
            Se muestra solo en el chat (en el formulario de reporte sería ruido). */}
        {vista === 'chat' && wa && !wa.ventanaAbierta && wa.gustavoNumber && (
          <Box
            component="a"
            href={`https://wa.me/${wa.gustavoNumber.replace(/\D/g, '')}?text=${encodeURIComponent('Hola Gustavo!')}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: 1.75, py: 1.1, textDecoration: 'none',
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: alpha('#25D366', 0.09),
              transition: 'background-color .15s',
              '&:hover': { bgcolor: alpha('#25D366', 0.16) },
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                Recibí los avisos en tu WhatsApp
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.35 }}>
                {/* Se nombra el tiempo pasado porque es el MOTIVO: WhatsApp corta a las
                    24 h del último mensaje del agente. Sin decirlo, "escribime" suena a
                    capricho; diciéndolo, se entiende que es la única forma de reabrirlo. */}
                {wa.horasSinEscribir == null
                  ? 'Todavía no me escribiste por WhatsApp 😔, así que los avisos te llegan solo acá. Escribime y te aviso ahí.'
                  : 'Pasó más de un día desde tu último mensaje por WhatsApp 😔, así que los avisos te llegan solo acá. Escribime y vuelvo a avisarte por WhatsApp.'}
              </Typography>
            </Box>
            <Box sx={{
              flexShrink: 0, px: 1.25, py: 0.4, borderRadius: 5,
              bgcolor: '#25D366', color: '#fff', fontSize: 11.5, fontWeight: 800,
            }}>
              Escribir
            </Box>
          </Box>
        )}

        {/* Dos paneles lado a lado dentro del mismo marco. El chat NO se desmonta al
            ir al reporte: si se desmontara, volver significaría perder la conversación
            que el agente venía teniendo. */}
        {/* Carril con posicionamiento absoluto y NO con un flex del 200%.
            Con el carril porcentual el ancho dependía de cómo resolvieran los hijos su
            base flexible, y el formulario —que tiene filas anchas— lo estiraba: el
            translateX quedaba corrido y el reporte entraba a mitad de camino. Acá cada
            panel mide exacto lo que el marco (inset 0), el segundo arranca en left 100%
            y los dos se corren un 100% de SU PROPIO ancho. No hay porcentaje que se
            recalcule con el contenido. */}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Panel izquierdo — Gustavo */}
            <Box sx={{
              position: 'absolute', inset: 0,
              transform: vista === 'reportar' ? 'translateX(-100%)' : 'translateX(0)',
              transition: 'transform .32s cubic-bezier(.22,.61,.36,1)',
              display: 'flex', flexDirection: 'column', minHeight: 0,
            }}>
              {!examenPendiente && (
              <SupportPanel
                onOpenTickets={() => { setOpen(false); navigate('/help?tab=tickets'); }}
              />
              )}

              <LowBalanceBanner />

              <Box sx={{ flex: 1, minHeight: 0 }}>
                {activeConvId && loadingInitial ? (
                  /* Antes acá había una caja VACÍA: el modal quedaba en blanco mientras
                     bajaba el hilo y no había forma de saber si estaba cargando o si la
                     conversación se había perdido. (pedido del user, 25/08/2026) */
                  <CargandoHilo />
                ) : activeConvId && initialMessages && convAcargar === activeConvId ? (
                  <ChatPanel
                    key={`${activeConvId}-${chatKey}`}
                    initialConversationId={activeConvId}
                    initialMessages={initialMessages}
                    userName={user.name}
                    hayMasHistorial={historial.hayMas}
                    cargandoMasHistorial={historial.cargandoMas}
                    onCargarMasHistorial={historial.cargarMas}
                    offsetHistorial={historial.offset}
                    onConversationChange={handleConversationChange}
                    onStreamEnd={() => window.dispatchEvent(new CustomEvent('gustavo:action'))}
                  />
                ) : (
                  <ChatPanel
                    key={`new-${chatKey}`}
                    userName={user.name}
                    autoPrompt={autoPrompt}
                    onConversationChange={handleConversationChange}
                    onStreamEnd={() => window.dispatchEvent(new CustomEvent('gustavo:action'))}
                  />
                )}
              </Box>
            </Box>

            {/* Panel derecho — reporte. Se monta recién al entrar: así el formulario
                arranca limpio cada vez y no arrastra lo tipeado de una vez anterior. */}
            <Box sx={{
              position: 'absolute', top: 0, bottom: 0, left: '100%', width: '100%',
              transform: vista === 'reportar' ? 'translateX(-100%)' : 'translateX(0)',
              transition: 'transform .32s cubic-bezier(.22,.61,.36,1)',
              overflow: 'hidden',
            }}>
              {vista === 'reportar' && (
                <ReportBugDialog
                  inline
                  open
                  initialKind={reportKind}
                  pendingAttachment={pendingAttachment}
                  attachmentUploading={attachmentUploading}
                  attachmentError={attachmentError}
                  onClose={() => { setVista('chat'); setPendingAttachment(null); setAttachmentError(false); }}
                />
              )}
            </Box>
        </Box>
      </Dialog>
    </>
  );
}
